import { useCallback, useEffect, useMemo, useState } from "react";

import { useColumnManager } from "../../../SpreadsheetEditor";
import type {
	ColumnConfig,
	QuoteColumnType,
} from "../../../SpreadsheetEditor";
import type { ScopeItemRowDraft } from "../../ScopeItemRowsEditor";
import type {
	QuoteTemplate,
	QuoteTemplateRow,
} from "../../../../api/quote-templates-api";
import { useUnitsAndMeasures } from "newui/store";
import { type DraftSnapshot, generateLocalRowId } from "../types";

/**
 * Convert an API template row into a draft row.
 *
 * Both sides are cells-only. The wire format keys cells by column
 * `sort_order` (stable across the JSON round-trip); the draft keys cells by
 * the column's stable `localId`. Any legacy `r.name` value coming from the
 * API layer has already been folded into `cells[primaryDescSortOrder]` by
 * `normalizeTemplate`, so all we do here is remap `sort_order` → `localId`.
 */
const hydrateApiRowToDraft = (
	row: QuoteTemplateRow,
	columns: ColumnConfig[],
	draftId: number
): ScopeItemRowDraft => {
	const sortOrderToLocalId = new Map<number, string>(
		columns.map((c) => [c.sortOrder, c.localId])
	);
	const cells: Record<string, string> = {};
	for (const [k, v] of Object.entries(row.cells)) {
		const localId = sortOrderToLocalId.get(Number(k));
		if (localId !== undefined) cells[localId] = v;
	}
	return {
		id: draftId,
		unitId: row.unit_id,
		sortOrder: row.sort_order,
		cells,
	};
};

/**
 * Owns the entire draft state for the template editor:
 *   - scalar fields (name, description, quote-table-name)
 *   - the column manager (delegated to useColumnManager)
 *   - the row list, with all mutators (add / remove / change / paste / reorder)
 *   - row selection state (select-all + bulk-delete)
 *   - derived data: unit options, the unit-validation set, the snapshot for undo
 *
 * The hook exposes a few high-level lifecycle helpers (`clearDraft`,
 * `loadTemplate`, `cloneTemplate`, `restoreSnapshot`) so the orchestrator can
 * switch between blank / edit / clone / undo states without poking individual
 * setters.
 */
export function useTemplateDraft() {
	const [draftName, setDraftName] = useState("");
	const [draftDescription, setDraftDescription] = useState("");
	const [draftQuoteTableName, setDraftQuoteTableName] = useState("");
	const [draftRows, setDraftRows] = useState<ScopeItemRowDraft[]>([]);
	const [selectedRowIds, setSelectedRowIds] = useState<Set<number>>(new Set());

	const columnManager = useColumnManager();
	const {
		columns,
		resetForCreate,
		resetForEdit,
		pendingDeleteId,
		executeColumnDelete,
	} = columnManager;

	const { values: unitsAndMeasures } = useUnitsAndMeasures();

	const unitOptions = useMemo(
		() =>
			(unitsAndMeasures ?? []).map((u) => ({
				label: u.label,
				value: u.value,
			})),
		[unitsAndMeasures]
	);

	// Templates have a simpler UX than live quote tables: a pending delete from
	// the column manager (last response_value while a total exists) should
	// auto-confirm instead of prompting.
	useEffect(() => {
		if (pendingDeleteId !== null) {
			executeColumnDelete(pendingDeleteId);
		}
	}, [pendingDeleteId, executeColumnDelete]);

	// -----------------------------------------------------------------------
	// Row mutators
	// -----------------------------------------------------------------------

	const handleAddRow = useCallback(() => {
		setDraftRows((prev) => [
			...prev,
			{
				id: generateLocalRowId(),
				unitId: null,
				sortOrder: prev.length + 1,
				cells: {},
			},
		]);
	}, []);

	/**
	 * Insert a row using a caller-supplied id. Used by the guided tour so the
	 * step's target testid (which encodes the row id) can be known at build
	 * time, then the row is created on `onBeforeStep` and removed on
	 * `onAfterStep`.
	 */
	const addRowWithId = useCallback((id: number) => {
		setDraftRows((prev) => {
			if (prev.some((r) => r.id === id)) return prev;
			return [
				...prev,
				{
					id,
					unitId: null,
					sortOrder: prev.length + 1,
					cells: {},
				},
			];
		});
	}, []);

	/**
	 * Update a single cell value. Cells are keyed by the column's stable
	 * `localId` so that reorders / type changes never shift values to the
	 * wrong column. This applies to ALL editable cell types (description
	 * columns now included — the legacy `row.name` slot is gone).
	 */
	const handleCellChange = useCallback(
		(rowId: number, columnLocalId: string, value: string) => {
			setDraftRows((prev) =>
				prev.map((r) =>
					r.id === rowId
						? {
								...r,
								cells: { ...(r.cells ?? {}), [columnLocalId]: value },
							}
						: r
				)
			);
		},
		[]
	);

	/**
	 * Excel/Sheets paste: tab-separated values across, newline-separated rows
	 * down. The pasted block is anchored at the cell that fired the paste
	 * (rowId + columnLocalId). For each cell in the matrix we find the visible
	 * column at `startColIndex + dx` and route the value:
	 *   - any description column    -> row.cells[col.localId] (uniform —
	 *     no special "first description" slot, cells-only model)
	 *   - the UoM column            -> row.unitId (match by id or label, case-insensitive)
	 *   - anything else (Value, Quantity, Text, Number, Total) -> skipped (bidder-fill)
	 *
	 * Rows beyond the existing draft are appended automatically. Unmatched
	 * cells leave the destination blank; the row is still created so the
	 * pasted block stays aligned vertically.
	 */
	const handlePasteMatrix = useCallback(
		(rowId: number, columnLocalId: string, text: string) => {
			const matrix = text
				.replace(/\r\n/g, "\n")
				.replace(/\r/g, "\n")
				.split("\n")
				.filter((line, idx, arr) => !(idx === arr.length - 1 && line === ""))
				.map((line) => line.split("\t"));
			if (matrix.length === 0) return;

			const startColIndex = columns.findIndex((c) => c.localId === columnLocalId);
			if (startColIndex === -1) return;

			const unitCol =
				columns.find((c) => c.columnType === "unit_of_measurement") ?? null;

			const unitIdSet = new Set(unitOptions.map((u) => u.value));
			const unitLabelToId = new Map<string, number>();
			for (const u of unitOptions) {
				unitLabelToId.set(u.label.trim().toLowerCase(), u.value);
			}
			const resolveUnit = (raw: string): number | null => {
				const trimmed = raw.trim();
				if (trimmed === "") return null;
				const asNum = Number(trimmed);
				if (Number.isFinite(asNum) && unitIdSet.has(asNum)) return asNum;
				const byLabel = unitLabelToId.get(trimmed.toLowerCase());
				return byLabel ?? null;
			};

			setDraftRows((prev) => {
				const startRowIndex = prev.findIndex((r) => r.id === rowId);
				if (startRowIndex === -1) return prev;

				const next = [...prev];
				for (let dy = 0; dy < matrix.length; dy++) {
					const targetIndex = startRowIndex + dy;

					if (targetIndex >= next.length) {
						next.push({
							id: generateLocalRowId(),
							unitId: null,
							sortOrder: next.length + 1,
							cells: {},
						});
					}

					const targetRow = { ...next[targetIndex] };
					targetRow.cells = { ...(targetRow.cells ?? {}) };

					const pastedRow = matrix[dy];
					for (let dx = 0; dx < pastedRow.length; dx++) {
						const destCol = columns[startColIndex + dx];
						if (!destCol) break;
						const value = pastedRow[dx];

						if (destCol.columnType === "description") {
							targetRow.cells[destCol.localId] = value;
						} else if (unitCol && destCol.localId === unitCol.localId) {
							targetRow.unitId = resolveUnit(value);
						}
					}

					next[targetIndex] = targetRow;
				}

				return next.map((r, i) => ({ ...r, sortOrder: i + 1 }));
			});
		},
		[columns, unitOptions]
	);

	const handleRemoveRow = useCallback((rowId: number) => {
		setDraftRows((prev) =>
			prev
				.filter((r) => r.id !== rowId)
				.map((r, i) => ({ ...r, sortOrder: i + 1 }))
		);
		setSelectedRowIds((prev) => {
			if (!prev.has(rowId)) return prev;
			const next = new Set(prev);
			next.delete(rowId);
			return next;
		});
	}, []);

	const handleRowChange = useCallback(
		(rowId: number, field: "unitId", value: number | null) => {
			setDraftRows((prev) =>
				prev.map((r) => (r.id === rowId ? { ...r, [field]: value } : r))
			);
		},
		[]
	);

	const handleReorderRows = useCallback(
		(sourceIndex: number, destIndex: number) => {
			setDraftRows((prev) => {
				if (
					sourceIndex < 0 ||
					destIndex < 0 ||
					sourceIndex >= prev.length ||
					destIndex >= prev.length
				) {
					return prev;
				}
				const next = [...prev];
				const [moved] = next.splice(sourceIndex, 1);
				next.splice(destIndex, 0, moved);
				return next.map((r, i) => ({ ...r, sortOrder: i + 1 }));
			});
		},
		[]
	);

	// -----------------------------------------------------------------------
	// Selection
	// -----------------------------------------------------------------------

	const handleToggleRowSelected = useCallback((rowId: number) => {
		setSelectedRowIds((prev) => {
			const next = new Set(prev);
			if (next.has(rowId)) next.delete(rowId);
			else next.add(rowId);
			return next;
		});
	}, []);

	const handleToggleSelectAll = useCallback(() => {
		setSelectedRowIds((prev) => {
			if (prev.size === draftRows.length && draftRows.length > 0) {
				return new Set();
			}
			return new Set(draftRows.map((r) => r.id));
		});
	}, [draftRows]);

	const handleBulkRemoveRows = useCallback(() => {
		if (selectedRowIds.size === 0) return;
		setDraftRows((prev) =>
			prev
				.filter((r) => !selectedRowIds.has(r.id))
				.map((r, i) => ({ ...r, sortOrder: i + 1 }))
		);
		setSelectedRowIds(new Set());
	}, [selectedRowIds]);

	// -----------------------------------------------------------------------
	// Lifecycle helpers
	// -----------------------------------------------------------------------

	const clearDraft = useCallback(() => {
		setDraftName("");
		setDraftDescription("");
		setDraftQuoteTableName("");
		setDraftRows([]);
		setSelectedRowIds(new Set());
		resetForCreate();
	}, [resetForCreate]);

	const loadTemplate = useCallback(
		(template: QuoteTemplate) => {
			setDraftName(template.name);
			setDraftDescription(template.description ?? "");
			setDraftQuoteTableName(template.quote_table_name ?? "");
			const editColumns: ColumnConfig[] = template.columns.map((c, idx) => ({
				id: c.id,
				columnType: c.column_type as QuoteColumnType,
				label: c.label,
				localId: `col-${c.id}-${idx}`,
				sortOrder: c.sort_order,
			}));
			resetForEdit(editColumns);
			setDraftRows(
				template.rows.map((r) =>
					hydrateApiRowToDraft(r, editColumns, r.id)
				)
			);
			setSelectedRowIds(new Set());
		},
		[resetForEdit]
	);

	const cloneTemplate = useCallback(
		(template: QuoteTemplate, suffix: string) => {
			setDraftName(`${template.name}${suffix}`);
			setDraftDescription(template.description ?? "");
			setDraftQuoteTableName(template.quote_table_name ?? "");
			const cloned: ColumnConfig[] = template.columns.map((c, idx) => ({
				id: undefined,
				columnType: c.column_type as QuoteColumnType,
				label: c.label,
				localId: `col-clone-${c.id}-${idx}-${Date.now()}`,
				sortOrder: c.sort_order,
			}));
			resetForEdit(cloned);
			setDraftRows(
				template.rows.map((r) =>
					hydrateApiRowToDraft(r, cloned, generateLocalRowId())
				)
			);
			setSelectedRowIds(new Set());
		},
		[resetForEdit]
	);

	const restoreSnapshot = useCallback(
		(snapshot: DraftSnapshot) => {
			setDraftName(snapshot.name);
			setDraftDescription(snapshot.description);
			setDraftQuoteTableName(snapshot.quoteTableName);
			setDraftRows(snapshot.rows.map((r) => ({ ...r })));
			resetForEdit(snapshot.columns.map((c) => ({ ...c })));
		},
		[resetForEdit]
	);

	// -----------------------------------------------------------------------
	// Derived
	// -----------------------------------------------------------------------

	const currentSnapshot = useMemo<DraftSnapshot>(
		() => ({
			name: draftName,
			description: draftDescription,
			quoteTableName: draftQuoteTableName,
			columns,
			rows: draftRows,
		}),
		[draftName, draftDescription, draftQuoteTableName, columns, draftRows]
	);

	const hasUnitColumn = useMemo(
		() => columns.some((c) => c.columnType === "unit_of_measurement"),
		[columns]
	);

	const rowsMissingUnit = useMemo<Set<number>>(() => {
		if (!hasUnitColumn) return new Set();
		const ids = draftRows
			.filter((r) => r.unitId === null || r.unitId === undefined)
			.map((r) => r.id);
		return new Set(ids);
	}, [hasUnitColumn, draftRows]);

	return {
		// scalar state
		draftName,
		setDraftName,
		draftDescription,
		setDraftDescription,
		draftQuoteTableName,
		setDraftQuoteTableName,

		// row state
		draftRows,
		selectedRowIds,

		// row handlers
		handleAddRow,
		addRowWithId,
		handleCellChange,
		handlePasteMatrix,
		handleRemoveRow,
		handleRowChange,
		handleReorderRows,
		handleToggleRowSelected,
		handleToggleSelectAll,
		handleBulkRemoveRows,

		// column manager (re-exposed in full for the SpreadsheetGrid)
		columnManager,

		// lifecycle
		clearDraft,
		loadTemplate,
		cloneTemplate,
		restoreSnapshot,

		// derived
		currentSnapshot,
		rowsMissingUnit,
		unitOptions,
	};
}

export type TemplateDraftApi = ReturnType<typeof useTemplateDraft>;

/**
 * Convert a draft row back into the wire shape expected by
 * `QuoteTemplateRowPayload`. Cells are re-keyed from `localId` (stable in the
 * draft) → the column's current `sort_order` (stable in storage). There is
 * no separate `name` field — descriptions are cells.
 */
export const dehydrateDraftRowForSave = (
	row: ScopeItemRowDraft,
	columns: ColumnConfig[]
): {
	unit_id: number | null;
	sort_order: number;
	cells: Record<number, string>;
} => {
	const localIdToSortOrder = new Map<string, number>(
		columns.map((c) => [c.localId, c.sortOrder])
	);
	const cells: Record<number, string> = {};
	for (const [localId, value] of Object.entries(row.cells ?? {})) {
		const sortOrder = localIdToSortOrder.get(localId);
		if (sortOrder !== undefined) cells[sortOrder] = value;
	}
	return {
		unit_id: row.unitId,
		sort_order: row.sortOrder,
		cells,
	};
};
